/** @jsx isolateComponent */

import { isNil } from 'lodash';
import React, { Fragment, ReactNode } from 'react';

import { isolateComponent } from '@zapier/common-utils';
import { Text } from '@zapier/design-system';

import { Price } from '@zapier/design-system-revenue/src/components/other';
import { FancyPrice } from '@zapier/design-system-revenue/src/components/private';
import { Currency, PlanV2 } from '@zapier/design-system-revenue/src/types';
import { isMonthly, isYearly } from '@zapier/design-system-revenue/src/utils';

type PlanPriceProps = {
  /** The type of currency to display. */
  currency: Currency;
  /** An array of identical plans with different variants */
  planVariants: PlanV2[];
  /** Shows billing details below price if true. Hides billing details otherwise. */
  showDetails?: boolean;
};

const Details: React.FC = ({ children }) => (
  <p>
    <Text color="grey700" type="altText800">
      {children}
    </Text>
  </p>
);

const getDetails = (
  currency: Currency,
  monthlyPlan?: PlanV2,
  yearlyPlan?: PlanV2
): ReactNode | undefined | null => {
  let line1 = null;
  let line2 = null;

  // User's on a free plan
  if (monthlyPlan && monthlyPlan.amount === 0) {
    line1 = 'free forever';
  }

  // User's on a non-legacy plan that's not free plan
  else if (monthlyPlan && yearlyPlan && monthlyPlan.amount !== 0) {
    line1 = 'per month, billed annually';
    line2 = (
      <Details>
        or <Price currency={currency}>{monthlyPlan.amount}</Price>{' '}
        month-to-month
      </Details>
    );
  }

  // User's on a legacy plan (only their currently selected interval is available)
  else {
    line1 = `per month, billed ${monthlyPlan ? 'monthly' : 'annually'}`;
  }

  return (
    <div>
      <Details>{line1}</Details>
      {line2}
    </div>
  );
};

/**
 * A component that shows a plan's price and billing details.
 */
export const PlanPrice = ({
  currency,
  showDetails = true,
  planVariants,
}: PlanPriceProps) => {
  const monthly = planVariants.find(plan => isMonthly(plan.interval));
  const yearly = planVariants.find(plan => isYearly(plan.interval));
  const details = getDetails(currency, monthly, yearly);
  const payment = yearly ? yearly.amount / 12 : monthly?.amount ?? 0;

  return (
    <Fragment>
      {!isNil(payment) && (
        <FancyPrice currency={currency}>{payment}</FancyPrice>
      )}
      {showDetails && details}
    </Fragment>
  );
};
